import { tap, finalize } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { Injectable } from '@angular/core';

import { AuthStore } from '@/app/core/store/auth';
import { AuthService, CredentialsInfo } from '@/app/api/auth';
import { SubscriptionsStore } from '@/app/core/store/subscriptions';
import { NamedRouter, NamedRouterNavigationExtras } from '@/app/core/services/named-router';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private TOKEN_COOKIE_KEY = 'token';

  constructor(
    private authStore: AuthStore,
    private namedRouter: NamedRouter,
    private authService: AuthService,
    private cookieService: CookieService,
    private subscriptionsStore: SubscriptionsStore,
  ) {}

  isAuthenticated() {
    return this.cookieService.check(this.TOKEN_COOKIE_KEY);
  }

  getToken() {
    return this.cookieService.get(this.TOKEN_COOKIE_KEY);
  }

  private _deleteCredentials() {
    this.cookieService.delete(this.TOKEN_COOKIE_KEY, '/');
    this.authStore.setAuthUser(null);
  }

  private _saveToken(tokenInfo: CredentialsInfo['token']) {
    this.cookieService.delete(this.TOKEN_COOKIE_KEY, '/');
    this.cookieService.set(this.TOKEN_COOKIE_KEY, tokenInfo.key, {
      expires: new Date(tokenInfo.refresh_expires_at),
      secure: true,
      sameSite: 'Strict',
      path: '/',
    });
  }

  private _saveCredentials(data: CredentialsInfo) {
    this.authStore.setAuthUser(data.user);
    this._saveToken(data.token);

    const subscription = data.user?.client?.subscription;
    if (subscription) {
      this.subscriptionsStore.setSubscription(subscription);
    }
  }

  sendLoginLink(email: string) {
    return this.authService.sendLoginLink(email);
  }

  verifyLoginLink(email: string, code: string) {
    return this.authService
      .verifyLoginLink(email, code)
      .pipe(tap((credentialInfo) => this._saveCredentials(credentialInfo)));
  }

  navigateToLogin(extras?: NamedRouterNavigationExtras) {
    this._deleteCredentials();
    this.namedRouter.navigateByName('login', extras);
  }

  logout() {
    return this.authService.logout().pipe(finalize(() => this.navigateToLogin()));
  }

  refreshToken() {
    return this.authService.refreshToken().pipe(tap((response) => this._saveToken(response.token)));
  }
}
